// React
import { memo, useCallback, useState } from "react";

// Helpers
import { classNames } from "helpers";

import { FIELD_HIGHLIGHT_GROUPS, FIELD_LABELS } from "./consts";
import { InvoiceFieldHighlighterProps } from "./types";

/**
 * Označava na dokumentu odakle je koji podatak pročitan. Koordinate su u omjeru 0–1 prema veličini
 * stranice, pa okviri stoje na mjestu bez obzira na to koliko je prikaz uvećan. Polje na kojem je
 * korisnik dobiva punu boju, ostala samo tanku crtu — inače bi cijela stranica bila išarana.
 * Prelaskom miša preko okvira piše što je s tog mjesta završilo u obrascu, pa se veza vidi i
 * obrnutim smjerom, bez traženja po polju.
 */
const InvoiceFieldHighlighter: React.FC<InvoiceFieldHighlighterProps> = (props) => {
  const { fieldLocations, activeField, fieldValues = {} } = props;

  const [hoveredField, setHoveredField] = useState<string | null>(null);

  const handleMouseEnter = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    setHoveredField(event.currentTarget.dataset.field ?? null);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredField(null);
  }, []);

  return (
    <div className="pointer-events-none absolute inset-0">
      {Object.entries(fieldLocations).map(([field, boxes]) =>
        boxes.map((box, index) => {
          const isHovered = field === hoveredField;
          const isActive = field === activeField || isHovered;
          const group = FIELD_HIGHLIGHT_GROUPS[field] ?? "info";
          const value = fieldValues[field];

          return (
            <div
              key={`${field}-${index}`}
              data-field={field}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              className={classNames(
                "pointer-events-auto absolute rounded-sm transition-colors duration-150",
                isActive ? "border-2" : "border-b-2",
                group === "amount"
                  ? (isActive ? "border-sky-500 bg-sky-400/25" : "border-sky-400/60")
                  : (isActive ? "border-emerald-500 bg-emerald-400/25" : "border-emerald-400/60")
              )}
              style={{
                left: `${box.x1 * 100}%`,
                top: `${box.y1 * 100}%`,
                width: `${(box.x2 - box.x1) * 100}%`,
                height: `${(box.y2 - box.y1) * 100}%`,
              }}
            >
              {isHovered && (
                // Oznaka stoji iznad okvira, a kod prvog retka ispod njega — inače bi izašla
                // izvan stranice i bila odrezana.
                <div
                  className={classNames(
                    "absolute left-0 whitespace-nowrap rounded-sm bg-slate-900 px-2 py-1 text-xs text-white shadow-lg",
                    box.y1 < 0.05 ? "top-full mt-1" : "bottom-full mb-1"
                  )}
                >
                  <span className="text-slate-300">{FIELD_LABELS[field] ?? field}</span>
                  {value && <span className="ml-2 font-medium">{value}</span>}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default memo(InvoiceFieldHighlighter);
